import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, User, Phone, Sparkles, UserPlus, CheckCircle } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface SignupFormProps {
  onSignup: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
  }) => Promise<void>;
  onSwitchToLogin: () => void;
  loading?: boolean;
}

const SignupForm: React.FC<SignupFormProps> = ({
  onSignup,
  onSwitchToLogin,
  loading = false
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName) {
      newErrors.firstName = t('validation.required');
    }
    
    if (!formData.lastName) {
      newErrors.lastName = t('validation.required');
    }
    
    if (!formData.email) {
      newErrors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.email');
    }
    
    if (!formData.phone) {
      newErrors.phone = t('validation.required');
    }
    
    if (!formData.password) {
      newErrors.password = t('validation.required');
    } else if (formData.password.length < 8) {
      newErrors.password = t('validation.password');
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordMatch');
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      try {
        await onSignup({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password
        });
      } catch (error) {
        setErrors({ general: t('errors.general') });
      }
    }
  };

  const handleSocialSignup = (provider: 'google' | 'apple' | 'facebook') => {
    // Mock social signup
    console.log(`Social signup with ${provider}`);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Enhanced Header with Gradient */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl mb-4 shadow-lg">
          <UserPlus className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold bg-gradient-to-r from-green-500 to-blue-600 bg-clip-text text-transparent">
          {t('auth.signupTitle')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">
          {t('auth.signupSubtitle')}
        </p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('auth.firstName')}
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            error={errors.firstName}
            icon={<User size={20} />}
            placeholder="Hamza"
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-green-500"
          />
          <Input
            label={t('auth.lastName')}
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            error={errors.lastName}
            icon={<User size={20} />}
            placeholder="Aqel"
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-green-500"
          />
        </div>

        <Input
          label={t('auth.email')}
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          error={errors.email}
          icon={<Mail size={20} />}
          placeholder="haqel91@gmail.com"
          required
          className="transition-all duration-200 focus:ring-2 focus:ring-green-500"
        />

        <Input
          label={t('auth.phone')}
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          error={errors.phone}
          icon={<Phone size={20} />}
          placeholder="+962 77 456 7890"
          required
          className="transition-all duration-200 focus:ring-2 focus:ring-green-500"
        />

        <div className="space-y-4">
          <Input
            label={t('auth.password')}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            error={errors.password}
            icon={<Lock size={20} />}
            showPasswordToggle
            helpText={t('validation.password')}
            placeholder="123456"
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-green-500"
          />

          <Input
            label={t('auth.confirmPassword')}
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            error={errors.confirmPassword}
            icon={<Lock size={20} />}
            showPasswordToggle
            placeholder="123456"
            required
            className="transition-all duration-200 focus:ring-2 focus:ring-green-500"
          />
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
          loading={loading}
        >
          <span className="flex items-center justify-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            {t('navigation.signup')}
          </span>
        </Button>
      </form>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-brown-300 dark:border-brown-600" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-brown-900 text-brown-500 dark:text-brown-400">
              {t('auth.orContinueWith')}
            </span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <Button
            variant="outline"
            onClick={() => handleSocialSignup('google')}
            className="flex justify-center items-center h-12 border-2 border-gray-200 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 transform hover:scale-105"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C18.46 2.09 15.43 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSocialSignup('apple')}
            className="flex justify-center items-center h-12 border-2 border-gray-200 hover:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/20 transition-all duration-200 transform hover:scale-105"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSocialSignup('facebook')}
            className="flex justify-center items-center h-12 border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 transform hover:scale-105"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </Button>
        </div>
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('auth.hasAccount')}{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-green-600 hover:text-green-500 dark:text-green-400 font-semibold transition-colors duration-200 hover:underline"
          >
            {t('auth.signInHere')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignupForm;
